import { ReactElement, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { v4 as uuid } from 'uuid';

import { useIsMounted } from '../../utils/hooks/useIsMounted';
import { useIsomorphicLayoutEffect } from '../../utils/hooks/useIsomorphicLayoutEffect';

/**
 * Accessible accordion - p-1 on content ensures outline visibility
 * @param children
 * @param renderHeading
 * @param name - Accordion name
 * @param initiallyExpanded
 */
export const Accordion = ({
  children,
  renderHeading,
  initiallyExpanded = false,
  name = uuid(),
  onExpandChange,
}: {
  children: ReactElement;
  renderHeading: (state: boolean) => ReactElement;
  initiallyExpanded?: boolean;
  name?: string;
  onExpandChange?: (expanded: boolean) => void;
}): ReactElement => {
  const [isAnimated, setIsAnimated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const [closeAfterAnimation, setCloseAfterAnimation] = useState(!initiallyExpanded);
  const isMounted = useIsMounted();
  const animationClass = useMemo(
    () => (isExpanded ? 'animate-slidedown' : 'animate-slideup'),
    [isExpanded]
  );

  const onAnimationEnd = useCallback(() => {
    setIsAnimated(false);
  }, []);

  useIsomorphicLayoutEffect(() => {
    setCloseAfterAnimation(!isExpanded);
    if (isMounted()) {
      setIsAnimated(true);
    }
  }, [isExpanded, isMounted]);

  return (
    <div className="accordion">
      <button
        className="w-full p-1 text-left"
        type="button"
        aria-expanded={isExpanded}
        aria-controls={`controls-${name}`}
        id={`accordion-${name}`}
        onClick={() => {
          setCloseAfterAnimation(false);
          setIsExpanded(!isExpanded);
          onExpandChange?.(!isExpanded);
        }}
      >
        {renderHeading(isExpanded)}
      </button>
      <div
        className={classNames('grid-rows-0 grid overflow-hidden', { [animationClass]: isAnimated })}
        style={{
          gridTemplateRows: closeAfterAnimation ? 0 : 'none',
        }}
        id={`controls-${name}`}
        role="region"
        aria-labelledby={`accordion-${name}`}
        onAnimationEnd={onAnimationEnd}
      >
        <div className={`overflow-hidden`}>{children}</div>
      </div>
    </div>
  );
};
